/**
 * Interoperability Standards - Standards Compliance Assessment
 *
 * Per design doc: UX_Architecture_Redesign_v1.0.md (DC-448)
 * Configure organization-wide data exchange standards and compliance.
 * This is an ORGANIZATIONAL setting, not per-dataset.
 *
 * Key features:
 * - Initialize default standards (DCAT, GA4GH, CDISC, FHIR)
 * - Enable/disable standards for the organization
 * - Validate compliance for each standard
 * - View Interoperability Maturity score
 * - Full DCAT configuration dashboard (moved from Journey Bridge AI)
 * - Portfolio configuration settings
 *
 * @version 3.0.0
 * @date 2025-12-05
 */

import React, { useState, useEffect, useCallback } from 'react';
import { DCATCompliance } from '../../components/semantic/DCATCompliance';
import './governance-layout.css';

// Standard type from API
interface InteropStandard {
  standard_id: string;
  display_name: string;
  description: string;
  is_enabled: boolean;
  compliance_score: number;
  checklist_status: Array<{ item: string; status: 'pass' | 'fail' | 'partial' | 'unchecked' }>;
  issues: string[];
  last_validated: string | null;
}

// Aggregate stats from API
interface StandardsAggregate {
  total_standards: number;
  enabled_count: number;
  interoperability_score: number | null;
  enabled_standards: string[];
  last_validated: string | null;
}

// Portfolio configuration (DC-448)
interface PortfolioConfig {
  catalog_url: string;
  default_license: string;
  organization_id: string;
  publisher_name: string;
  contact_email: string;
}

// View mode for DCAT expanded configuration
type ViewMode = 'list' | 'dcat-details';

export const InteroperabilityStandards: React.FC = () => {
  const [standards, setStandards] = useState<InteropStandard[]>([]);
  const [aggregate, setAggregate] = useState<StandardsAggregate | null>(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [validating, setValidating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // DC-448: DCAT configuration view mode
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // DC-448: Portfolio configuration state
  const [portfolioConfig, setPortfolioConfig] = useState<PortfolioConfig>({
    catalog_url: '',
    default_license: 'CC-BY-4.0',
    organization_id: '',
    publisher_name: '',
    contact_email: '',
  });
  const [savingConfig, setSavingConfig] = useState(false);

  // Load standards on mount
  useEffect(() => {
    loadStandards();
  }, []);

  const loadStandards = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/organization/standards');
      if (!response.ok) {
        throw new Error(`Failed to load standards: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setStandards(data.standards || []);
        setAggregate(data.aggregate || null);
      } else {
        throw new Error(data.error || 'Failed to load standards');
      }
    } catch (err: unknown) {
      console.error('[InteroperabilityStandards] Load error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  // Initialize default standards
  const handleInitialize = useCallback(async () => {
    try {
      setInitializing(true);
      setError(null);

      const response = await fetch('/api/organization/standards/initialize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`Initialization failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        await loadStandards();
      } else {
        throw new Error(data.error || 'Initialization failed');
      }
    } catch (err: unknown) {
      console.error('[InteroperabilityStandards] Initialize error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setInitializing(false);
    }
  }, []);

  // Toggle standard enabled/disabled
  const handleToggle = useCallback(async (standardId: string, currentEnabled: boolean) => {
    try {
      setError(null);

      const response = await fetch(`/api/organization/standards/${standardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_enabled: !currentEnabled }),
      });

      if (!response.ok) {
        throw new Error(`Update failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        await loadStandards();
      } else {
        throw new Error(data.error || 'Update failed');
      }
    } catch (err: unknown) {
      console.error('[InteroperabilityStandards] Toggle error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    }
  }, []);

  // Validate standard compliance
  const handleValidate = useCallback(async (standardId: string) => {
    try {
      setValidating(standardId);
      setError(null);

      const response = await fetch(`/api/organization/standards/${standardId}/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`Validation failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        await loadStandards();
      } else {
        throw new Error(data.error || 'Validation failed');
      }
    } catch (err: unknown) {
      console.error('[InteroperabilityStandards] Validate error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setValidating(null);
    }
  }, []);

  // Get compliance level label
  const getComplianceLevel = (score: number): { label: string; class: string } => {
    if (score >= 90) return { label: 'Excellent', class: 'compliance--excellent' };
    if (score >= 70) return { label: 'Good', class: 'compliance--good' };
    if (score >= 50) return { label: 'Fair', class: 'compliance--fair' };
    if (score >= 30) return { label: 'Low', class: 'compliance--low' };
    return { label: 'Critical', class: 'compliance--critical' };
  };

  // Get checklist status icon
  const getChecklistIcon = (status: string): string => {
    const icons: Record<string, string> = {
      pass: '+',
      partial: '~',
      fail: 'x',
      unchecked: 'o',
    };
    return icons[status] || 'o';
  };

  // DC-448: Handle DCAT configuration click
  const handleConfigureDCAT = useCallback(() => {
    setViewMode('dcat-details');
  }, []);

  // DC-448: Handle back to list view
  const handleBackToList = useCallback(() => {
    setViewMode('list');
  }, []);

  // DC-448: Save portfolio configuration
  const handleSavePortfolioConfig = useCallback(async () => {
    try {
      setSavingConfig(true);
      setError(null);

      const response = await fetch('/api/organization/portfolio-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(portfolioConfig),
      });

      if (!response.ok) {
        throw new Error(`Save failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Save failed');
      }
    } catch (err: unknown) {
      console.error('[InteroperabilityStandards] Portfolio config save error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setSavingConfig(false);
    }
  }, [portfolioConfig]);

  // DC-448: Render DCAT Details View
  const renderDCATDetailsView = () => {
    const dcatStandard = standards.find(s => s.standard_id === 'dcat-ap');

    return (
      <div className="dcat-details-view">
        {/* Back Navigation */}
        <div className="dcat-details__nav">
          <button onClick={handleBackToList} className="btn btn--link">
            ← Back to Standards
          </button>
        </div>

        {/* DCAT Header */}
        <div className="dcat-details__header">
          <h2>DCAT-AP Configuration</h2>
          <p className="dcat-details__subtitle">
            Configure Data Catalog Vocabulary settings for your organization
          </p>
        </div>

        {/* Portfolio Configuration Section */}
        <div className="dcat-details__section">
          <h3>Portfolio Configuration</h3>
          <p className="section-description">
            These settings apply to all datasets exported as DCAT-AP compliant catalogs.
          </p>

          <div className="portfolio-config-form">
            <div className="form-group">
              <label htmlFor="catalog_url">Dataset Catalog URL</label>
              <input
                type="url"
                id="catalog_url"
                placeholder="https://catalog.example.com"
                value={portfolioConfig.catalog_url}
                onChange={(e) =>
                  setPortfolioConfig((prev) => ({ ...prev, catalog_url: e.target.value }))
                }
              />
              <small>The base URL where your data catalog is published</small>
            </div>

            <div className="form-group">
              <label htmlFor="default_license">Default License</label>
              <select
                id="default_license"
                value={portfolioConfig.default_license}
                onChange={(e) =>
                  setPortfolioConfig((prev) => ({ ...prev, default_license: e.target.value }))
                }
              >
                <option value="CC-BY-4.0">CC-BY-4.0 (Attribution)</option>
                <option value="CC-BY-SA-4.0">CC-BY-SA-4.0 (Attribution-ShareAlike)</option>
                <option value="CC-BY-NC-4.0">CC-BY-NC-4.0 (Attribution-NonCommercial)</option>
                <option value="CC0-1.0">CC0-1.0 (Public Domain)</option>
                <option value="proprietary">Proprietary</option>
                <option value="custom">Custom License</option>
              </select>
              <small>Default license applied to datasets without explicit licensing</small>
            </div>

            <div className="form-group">
              <label htmlFor="organization_id">Organization ID</label>
              <input
                type="text"
                id="organization_id"
                placeholder="org-12345"
                value={portfolioConfig.organization_id}
                onChange={(e) =>
                  setPortfolioConfig((prev) => ({ ...prev, organization_id: e.target.value }))
                }
              />
              <small>Unique identifier for your organization in DCAT exports</small>
            </div>

            <div className="form-group">
              <label htmlFor="publisher_name">Publisher Name</label>
              <input
                type="text"
                id="publisher_name"
                placeholder="Acme Pharmaceuticals"
                value={portfolioConfig.publisher_name}
                onChange={(e) =>
                  setPortfolioConfig((prev) => ({ ...prev, publisher_name: e.target.value }))
                }
              />
              <small>Organization name used in dcat:publisher field</small>
            </div>

            <div className="form-group">
              <label htmlFor="contact_email">Contact Email</label>
              <input
                type="email"
                id="contact_email"
                placeholder="data-steward@example.com"
                value={portfolioConfig.contact_email}
                onChange={(e) =>
                  setPortfolioConfig((prev) => ({ ...prev, contact_email: e.target.value }))
                }
              />
              <small>Contact point for data catalog inquiries</small>
            </div>

            <div className="form-actions">
              <button
                onClick={handleSavePortfolioConfig}
                disabled={savingConfig}
                className="btn btn--primary"
              >
                {savingConfig ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>

        {/* DCAT Compliance Dashboard */}
        <div className="dcat-details__section">
          <h3>DCAT Compliance Dashboard</h3>
          <p className="section-description">
            Analyze your datasets for DCAT-AP 2.1 compliance and export to RDF.
          </p>
          <DCATCompliance />
        </div>

        {/* Standard Status Summary */}
        {dcatStandard && (
          <div className="dcat-details__section">
            <h3>Standard Status</h3>
            <div className="standard-status-summary">
              <div className="status-item">
                <span className="status-label">Enabled:</span>
                <span className={`status-value ${dcatStandard.is_enabled ? 'enabled' : 'disabled'}`}>
                  {dcatStandard.is_enabled ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Compliance Score:</span>
                <span className="status-value">{Math.round(dcatStandard.compliance_score)}%</span>
              </div>
              <div className="status-item">
                <span className="status-label">Last Validated:</span>
                <span className="status-value">
                  {dcatStandard.last_validated
                    ? new Date(dcatStandard.last_validated).toLocaleDateString()
                    : 'Never'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="governance-loading">
        <div className="spinner" />
        <p>Loading interoperability standards...</p>
      </div>
    );
  }

  // DC-448: Render DCAT details view if in that mode
  if (viewMode === 'dcat-details') {
    return (
      <div className="governance-section interoperability-standards">
        {/* Error Display */}
        {error && (
          <div className="governance-error">
            <span className="governance-error__icon">!</span>
            <span className="governance-error__message">{error}</span>
            <button onClick={() => setError(null)} className="governance-error__dismiss">
              X
            </button>
          </div>
        )}
        {renderDCATDetailsView()}
      </div>
    );
  }

  return (
    <div className="governance-section interoperability-standards">
      {/* Header */}
      <div className="governance-section__header">
        <div className="governance-section__title">
          <h2>Interoperability Standards</h2>
          <p className="governance-section__subtitle">
            Configure organization-wide data exchange standards for improved discoverability
          </p>
        </div>
        {standards.length === 0 && (
          <button
            onClick={handleInitialize}
            disabled={initializing}
            className="btn btn--primary"
          >
            {initializing ? 'Initializing...' : 'Initialize Standards'}
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="governance-error">
          <span className="governance-error__icon">!</span>
          <span className="governance-error__message">{error}</span>
          <button onClick={() => setError(null)} className="governance-error__dismiss">
            X
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="governance-summary">
        <div className="summary-card">
          <div className="summary-card__value">{aggregate?.total_standards || 0}</div>
          <div className="summary-card__label">Standards</div>
        </div>
        <div className="summary-card">
          <div className="summary-card__value">{aggregate?.enabled_count || 0}</div>
          <div className="summary-card__label">Enabled</div>
        </div>
        <div className="summary-card summary-card--primary">
          <div className="summary-card__value">
            {aggregate?.interoperability_score != null
              ? `${Math.round(aggregate.interoperability_score)}%`
              : '--'}
          </div>
          <div className="summary-card__label">Interop Score</div>
        </div>
        <div className="summary-card">
          <div className="summary-card__value">
            {aggregate?.last_validated
              ? new Date(aggregate.last_validated).toLocaleDateString()
              : '--'}
          </div>
          <div className="summary-card__label">Last Validated</div>
        </div>
      </div>

      {/* Standards List */}
      <div className="governance-list">
        {standards.length === 0 ? (
          <div className="governance-empty">
            <p>No interoperability standards configured yet.</p>
            <p className="governance-empty__hint">
              Click "Initialize Standards" to set up default standards including DCAT-AP, GA4GH, CDISC, and FHIR.
            </p>
          </div>
        ) : (
          <div className="standards-grid">
            {standards.map(standard => {
              const compliance = getComplianceLevel(standard.compliance_score);
              return (
                <div
                  key={standard.standard_id}
                  className={`standard-card ${standard.is_enabled ? 'standard-card--enabled' : ''}`}
                >
                  <div className="standard-card__header">
                    <div className="standard-card__info">
                      <h4>{standard.display_name}</h4>
                      <p className="standard-card__description">{standard.description}</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={standard.is_enabled}
                        onChange={() => handleToggle(standard.standard_id, standard.is_enabled)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  {standard.is_enabled && (
                    <>
                      <div className="standard-card__score">
                        <div className="compliance-meter">
                          <div
                            className={`compliance-meter__fill ${compliance.class}`}
                            style={{ width: `${standard.compliance_score}%` }}
                          />
                        </div>
                        <div className="compliance-info">
                          <span className={`compliance-label ${compliance.class}`}>
                            {compliance.label}
                          </span>
                          <span className="compliance-score">
                            {Math.round(standard.compliance_score)}%
                          </span>
                        </div>
                      </div>

                      {/* Checklist */}
                      {standard.checklist_status && standard.checklist_status.length > 0 && (
                        <div className="standard-card__checklist">
                          {standard.checklist_status.map((item, idx) => (
                            <div
                              key={idx}
                              className={`checklist-item checklist-item--${item.status}`}
                            >
                              <span className="checklist-icon">
                                [{getChecklistIcon(item.status)}]
                              </span>
                              <span className="checklist-text">{item.item}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Issues */}
                      {standard.issues && standard.issues.length > 0 && (
                        <div className="standard-card__issues">
                          {standard.issues.map((issue, idx) => (
                            <div key={idx} className="issue-item">
                              [!] {issue}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="standard-card__actions">
                        <button
                          onClick={() => handleValidate(standard.standard_id)}
                          disabled={validating === standard.standard_id}
                          className="btn btn--small btn--secondary"
                        >
                          {validating === standard.standard_id ? 'Validating...' : 'Validate'}
                        </button>
                        {/* DC-448: Add Configure button for DCAT-AP */}
                        {standard.standard_id === 'dcat-ap' && (
                          <button
                            onClick={handleConfigureDCAT}
                            className="btn btn--small btn--primary"
                          >
                            Configure
                          </button>
                        )}
                        {standard.last_validated && (
                          <span className="standard-card__last-validated">
                            Last: {new Date(standard.last_validated).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="governance-help">
        <h4>About Interoperability Maturity</h4>
        <p>
          Interoperability Maturity measures compliance with data exchange standards.
          It contributes <strong>25%</strong> to the Execution Risk coefficient.
        </p>
        <p>
          Enable standards relevant to your organization (DCAT for cataloging, GA4GH for genomics,
          CDISC for clinical data, FHIR for healthcare). Validation checks your datasets against
          each standard's requirements.
        </p>
      </div>
    </div>
  );
};

export default InteroperabilityStandards;
